import type { Bundle, Patient } from 'fhir/r4';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import React from 'react';
import { DatesRangeInput } from 'semantic-ui-calendar-react';
import { Loader, Message, Icon, Button, Form, Confirm } from 'semantic-ui-react';
import type { StrictCheckboxProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type { Fetched, FlatPatient, FoodOrder, FoodRecommendation, OrderNote } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import { MultiFetcher } from '../../../../data/FHIRFetcher';
import FoodFetcher, { } from '../../../../data/FoodFetcher';
import * as FHIRUtils from '../../../../utils/FHIRUtils';
import { mapKey } from '../../../../utils/mapKey';

import { statusColors } from './constants';
import FoodRecommendationsTable from './table';

/**
 * The sort-and-filter state behind the food-recommendations table. Local rather
 * than shared with the orders reducers: this one filters on `createdDateTime`
 * where those filter on `orderDate`.
 */
type FoodRecommendationsTableState = {
  // `null` from the seed, `undefined` from SWR's `data`: both really occur.
  data: FoodRecommendation[] | null | undefined;
  direction: 'ascending' | 'descending' | null;
  sortcolumn: FoodRecommendationsTableAction['sortcolumn'] | null;
};

/** Every action this reducer is dispatched, from the four `dispatch({...})` calls below. */
type FoodRecommendationsTableAction = {
  data?: FoodRecommendation[] | null | undefined;
  datevalue?: string | null;
  /**
   * A column NAME from the sort headers, or the lodash ITERATEE FUNCTION the post-load
   * `CHANGE_SORT` passes. Like the `draftOrders` twin and unlike the `orders` one —
   * which subscripts by this value and so stringifies a function into a property name
   * — this reducer hands it straight to lodash, so the default sort works here.
   */
  sortcolumn?: _.ListIteratee<FoodRecommendation>;
  type: string;
};

/**
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. Third copy of the reducer in
 * `../orders/index.tsx` and `../draftOrders/index.tsx`, with the same absences:
 * `data` is seeded `null` so `state.data.slice()` throws on an early
 * `CHANGE_SORT`, and `FILTER` dereferences an `action.data` its own type marks
 * optional. Masked because every dispatch site passes an array.
 */
function Reducer(
  state: FoodRecommendationsTableState,
  action: FoodRecommendationsTableAction
): FoodRecommendationsTableState {
  switch (action.type) {
    case 'CHANGE_SORT':
      if (state.sortcolumn === action.sortcolumn) {
        return {
          ...state,
          data: state.data!.slice().reverse(),
          direction: state.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return {
        sortcolumn: action.sortcolumn,
        // Asserted, not defaulted: an `undefined` in this array steers overload
        // resolution onto `sortBy(T extends object, …)`, which returns the wrong
        // element type rather than reporting the absence.
        data: _.sortBy(state.data, [action.sortcolumn!]),
        direction: 'ascending',
      };
    case 'FILTER':
      return {
        ...state,
        data: action.data!.filter((d) => {
          return (
            moment(d.createdDateTime).local().format('YYYY-MM-DD') === moment(action.datevalue).format('YYYY-MM-DD')
          );
        }),
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        data: action.data,
        sortcolumn: null,
      };
    default:
      throw new Error();
  }
}
/**
 * What the food-recommendations screen accepts. Two JSX call sites in
 * `../index.tsx`, splitting on `patientId` as the other food screens do.
 *
 * As with `../draftOrders`, most of what is passed goes unread: `contractsME`,
 * `profileData`, `vendorName`, `ordersMealEquivalents`,
 * `setOrdersMealEquivalents` and `programMAXMealEquivalents` are all inert here.
 * The three food screens are fed from one shared block in `../index.tsx`
 * regardless of which consumes what.
 */
type FoodRecommendationsProps = {
  /** Accepted but unread. */
  contractsME?: Record<string, string | number>;
  /** Opens the roster-wide member drawer; called unguarded. */
  onPatientSelected?: (patient: unknown) => void;
  /** Accepted but unread. */
  ordersMealEquivalents?: number;
  /** The member, already flattened; read only to source the table's `org`. */
  patient?: FlatPatient;
  /** Absent or null selects the roster-wide mode. */
  patientId?: string | null;
  /** Accepted but unread. */
  profileData?: Bundle | Record<string, never>;
  /** Accepted but unread. */
  programMAXMealEquivalents?: number;
  /** The org filter for the roster-wide mode. */
  selectedOrg?: string;
  /** Accepted but unread. */
  setOrdersMealEquivalents?: (mealEquivalents: number) => void;
  /** A vendor code, or the literal 'All'. */
  vendor?: string;
  /** Accepted but unread. */
  vendorName?: string;
};

function FoodRecommendations(props: FoodRecommendationsProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;
  // Seeded `''` but `resetFilters` writes `null`; neither is read, which is why the
  // two producers disagreeing has never shown.
  const [_statusvalue, setStatusValue] = useState<string | null>('');
  const [_dateValue, setDateValue] = useState<string | null>(null);
  // `undefined` comes from `selectAll` — see the SUSPECTED DEFECT there.
  const [selectedOrderIds, setselectedOrderIds] = useState<(string | undefined)[]>([]);
  const [selectedOrdersLength, setselectedOrdersLength] = useState(0);
  const [ordersMessage, setOrdersMessage] = useState('');
  const [working, _setWorking] = useState(false);
  const [workingon, _setWorkingOn] = useState('');
  const [hideMessage, setHideMessage] = useState(false);
  const [_open, _setOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<FoodOrder>({});
  const [searchId, setSearchId] = useState('');
  const [dateState, setDateState] = useState('');
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [ordersUpdateError, _setOrdersUpdateError] = useState(false);
  // Both are maps keyed by patient id, accumulated across the paged /Patient fetches below.
  const [rawPatients, setRawPatients] = useState<Record<string, unknown> | null>(null);
  const [patients, setPatients] = useState<Record<string, FlatPatient> | null>(null);
  const [_patientObservations, _setPatientObservations] = useState({});

  const _PATIENT_ATTRIBUTES = [
    'firstName',
    'lastName',
    'delivery_addressLine1',
    'delivery_addressLine2',
    'delivery_city',
    'delivery_state',
    'delivery_zip',
    'email',
    'homePhone',
  ];
  const [loading, setLoading] = React.useState(true);

  const invoiceStatusList = ['partially-fulfilled', 'unfulfilled', 'delivered', 'reshipped'];
  // Annotated because SWR 0.5.7 defaults `Data` to `any` and a detached options
  // `const` infers nothing from its `useSWR` call site.
  const foodRecommendationsOptions: SWRConfiguration<Fetched<FoodRecommendation[]>> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data) => {
      setLoading(false);
      dispatch({ type: 'CLEAR_FILTERS', data: data });
      dispatch({
        type: 'CHANGE_SORT',
        sortcolumn: (od) => {
          return [statusColors[mapKey(od.status)], od.createdDateTime];
        },
        data: data,
      });
      if (data && data.length) {
        const patientsObj = data.map((order) => {
          return { id: order.patientId, org: order.clientOrgId };
        });
        // The `filter(c => c && …)` below drops the absent ids before they are joined
        // into the search URL.
        const orgPatients: Record<string, (string | undefined)[]> = {};
        patientsObj.forEach((patient) => {
          if (patient.org) {
            if (Object.prototype.hasOwnProperty.call(orgPatients, patient.org)) {
              // The `hasOwnProperty` test above is what makes this read safe;
              // noUncheckedIndexedAccess cannot see through it.
              orgPatients[patient.org]!.push(patient.id);
            } else {
              orgPatients[patient.org] = [patient.id];
            }
          }
        });
        const urls: string[] = [];

        if (Object.keys(orgPatients).length) {
          Object.keys(orgPatients).forEach((org) => {
            const patients = orgPatients[org]!;
            const uniquePatients = patients.filter((c, index) => {
              if (c && c !== '') return patients.indexOf(c) === index;
            });
            if (uniquePatients && uniquePatients.length) {
              const groups = uniquePatients
                .map((e, i) => {
                  return i % 50 === 0 ? uniquePatients.slice(i, i + 50) : null;
                })
                .filter((e): e is (string | undefined)[] => Boolean(e));
              // eslint-disable-next-line @typescript-eslint/no-for-in-array -- `for...in` over an array is the existing traversal; changing it to `for...of` would alter the index type from string to element and is a behaviour change, not an annotation. The rule became visible only once `groups` stopped being `any`.
              for (const groupIndex in groups) {
                urls.push(`/Patient/?organization=${org}&_id=${groups[groupIndex]!.join(',')}`);
              }
            }
          });
          getPatients(urls);
          // getPatientObservations(observationsUrl);
        } else {
          setLoading(false);
        }
      }
    },
  };

  const getPatients = (urls: string[]) => {
    MultiFetcher<Bundle>(authInfo.token, ...urls).then(
      (patientsDataset) => {
        // These accumulate into id-keyed maps via the object spreads below, so they are records.
        // The seed was `[]`, which the first spread already collapsed to `{}` — and the sole
        // consumer only ever does `patients && patients[id]`, so the seed shape is unobservable.
        // Values are `parseFHIRPatient` output, i.e. `FlatPatient`, not `unknown` —
        // stated so the map satisfies the table's `patients` prop without a cast.
        let combinedPatients: Record<string, FlatPatient> = {};
        let combinedRawPatients: Record<string, unknown> = {};
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `MultiFetcher` resolves `undefined`,
         * not `[]`, when handed no URLs, so this `.forEach` throws on that arm.
         *
         * Reachable: the caller's gate is `Object.keys(orgPatients).length`, while
         * `urls` is only pushed to under the inner `uniquePatients.length` test. A
         * roster whose every `patientId` is absent or `''` passes the outer gate and
         * pushes nothing.
         *
         * Asserted rather than optional-chained: `!` erases, so the throw is preserved.
         */
        patientsDataset!.forEach((patientsData) => {
          if (patientsData && patientsData.entry && patientsData.entry.length) {
            const tempPatients: Record<string, FlatPatient> = {};
            const tempRawPatients: Record<string, unknown> = {};
            patientsData.entry.forEach((item) => {
              // `resource` is `0..1`. Asserted rather than narrowed: narrowing would
              // skip a malformed entry, silently dropping a member from the roster
              // where today the parse throws.
              tempPatients[item.resource!.id!] = FHIRUtils.parseFHIRPatient(
                item.resource as Patient
              );
              tempRawPatients[item.resource!.id!] = item.resource;
            });
            combinedPatients = { ...combinedPatients, ...tempPatients };
            combinedRawPatients = {
              ...combinedRawPatients,
              ...tempRawPatients,
            };
            setLoading(false);
          }
        });
        setPatients(combinedPatients);
        setRawPatients(combinedRawPatients);
      },
      (_error) => {}
    );
  };

  const url =
    '?clientOrgId=' +
    props.selectedOrg +
    (props.patientId
      ? props.vendor === 'All'
        ? `&patientId=` + props.patientId
        : `&vendorId=` + props.vendor + `&patientId=` + props.patientId
      : props.vendor !== 'All'
      ? `&vendorId=` + props.vendor
      : ``) +
    (searchId ? '&orderId=' + searchId : '') +
    (startRange
      ? '&createdDateTime:ge=' +
        moment(startRange).utc().format() +
        (endRange ? '&createdDateTime:le=' + moment(endRange).utc().add(1, 'days').format() : '')
      : '');

  const {
    data: foodRecommendationsData,
    error: foodRecommendationsError,
    mutate: _foodRecommendationsMutate,
    isValidating: datasetLoading,
  } = useSWR<Fetched<FoodRecommendation[]>, Error>(
    [`/food/food-recommendations` + url, authInfo.token],
    FoodFetcher,
    foodRecommendationsOptions
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
  foodRecommendationsData &&
    foodRecommendationsData?.sort((a, b) => {
      // Fourth instance of the undated-row comparator; see the SUSPECTED DEFECT note
      // in `../orders/index.tsx`. `FoodRecommendation.createdDateTime` is optional and
      // `new Date(undefined).getTime()` is `NaN`, which makes the whole sort's
      // ordering inconsistent rather than merely misplacing the undated row.
      return new Date(b.createdDateTime!).getTime() - new Date(a.createdDateTime!).getTime();
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
  foodRecommendationsData &&
    foodRecommendationsData?.map((order) => {
      const quantity =
        order.orderItems &&
        order.orderItems.length > 0 &&
        _.sum(
          order.orderItems.map((od) => {
            return parseInt(od.quantity);
          })
        );
      order.quantity = quantity;
    });

  // Dead since the member drawer stopped reading it; the assignment is kept so the
  // emitted program still matches upstream. Renaming it would not.
  let flatPatient;
  if (props.patientId) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    flatPatient = props.patient;
  }

  useEffect(() => {
    dispatch({ type: 'CLEAR_FILTERS', data: foodRecommendationsData });
    setOrdersMessage('');
    setselectedOrderIds([]);
    setselectedOrdersLength(0);
  }, [foodRecommendationsData]);

  const openSelectedPatient = (id?: string) => {
    if (rawPatients) {
      props.onPatientSelected!(rawPatients[mapKey(id)]);
    }
  };

  const resetFilters = () => {
    setStatusValue(null);
    setDateValue(null);
    setDateState('');
    setStartRange('');
    setEndRange('');
    setSearchId('');
    setOrdersMessage('');
    dispatch({ type: 'CLEAR_FILTERS', data: foodRecommendationsData });
  };

  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Third instance of the select-all shape in
   * `../orders/index.tsx` and `../draftOrders/index.tsx`: `selectedOrderIds` is
   * declared `string[]` but `FoodRecommendation.id` is optional, so a row the
   * recommender sent without one puts `undefined` into the selection.
   *
   * Not guarded: filtering the absent ids out would silently shrink a select-all the
   * care manager believes covers the page.
   */
  const selectAll = (_e: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => {
    if (checkprops.checked) {
      setselectedOrderIds(
        state.data!.map((d) => {
          return d.id;
        })
      );
      setselectedOrdersLength(state.data!.length);
    } else {
      setselectedOrderIds([]);
      setselectedOrdersLength(0);
    }
  };

  function addExportData(e: unknown, checkprops: StrictCheckboxProps, id?: string) {
    let temp = selectedOrderIds;
    if (checkprops.checked) {
      temp.push(id);
    } else {
      temp = temp.filter((o) => {
        return o !== id;
      });
    }
    setselectedOrderIds(temp);
    setselectedOrdersLength(temp.length);
  }

  /*
   * Unreferenced, as is `_handleEstimateDeliveryDateChange` below: this is an unwired
   * copy of the edit-order form in `food/orders/index.tsx`, whose live twin
   * (`handleChange` there) carries the reasoning for this parameter shape.
   *
   * It differs from that twin in one way worth recording: this copy takes the status
   * value off `e.target.textContent` rather than as a `value` argument, so `e` names
   * `textContent` in addition to the twin's two members. `target` names only the members
   * the body reads, for the same reason the twin does — the call sites that would have
   * constrained it do not exist.
   */
  const _handleChange = (
    e: { target?: { textContent?: string | null; value?: string; valueAsNumber?: number } },
    attributeName: string,
    previous?: OrderNote[]
  ) => {
    const temp = orderDetails;
    const value = e.target && e.target.textContent;
    if (attributeName === 'status' && (value === 'NEW' || value === 'EXPORTED')) {
      temp[attributeName] = value.toLowerCase();
    } else if (attributeName === 'bundleQuantity') {
      // Assigned as a STRING here where the live twin assigns the raw number to the
      // same field. Left as written: this path is dead, and changing it would be a
      // behaviour change to code no call site reaches.
      temp[attributeName] = (e.target &&
        e.target.valueAsNumber &&
        e.target.valueAsNumber.toString()) as unknown as number;
    } else if (
      attributeName === 'foodPricing' ||
      attributeName === 'shippingFees' ||
      attributeName === 'estimatedDeliveryDate'
    ) {
      temp[attributeName] = (e.target && e.target.value) as string;
    } else if (attributeName === 'orderNotes') {
      let notepayload: OrderNote[] = [];
      if (previous) {
        notepayload = [...previous];
      }
      const currentPayload: OrderNote = {
        author: practitionerName,
        time: moment().utc().format(),
        text: (e.target && e.target.value) as string,
      };
      notepayload.push(currentPayload);
      temp[attributeName] = [...notepayload];
    } else {
      temp[attributeName as 'status'] = value as string;
    }
    if (temp['status'] && invoiceStatusList.includes(temp['status'].toLowerCase())) {
      temp['invoiceReady'] = '1';
    }
    setOrderDetails(temp);
  };

  const _handleEstimateDeliveryDateChange = (
    e: unknown,
    estimatedDeliveryDate: { value?: unknown }
  ) => {
    const temp = orderDetails;
    temp['estimatedDeliveryDate'] = moment(estimatedDeliveryDate.value as string).format(
      'YYYY-MM-DD'
    );
    setOrderDetails(temp);
  };

  const handleSearchById = (e: unknown, searchProps: { value?: unknown }) => {
    setSearchId(searchProps.value as string);
  };

  const handleChangeDate = (event: unknown, { name: _name, value }: { name?: string; value: string }) => {
    setDateState(value);
    const temp = value.split(' - ');
    setStartRange(temp[0] as string);
    setEndRange(temp[1] as string);
  };

  const [state, dispatch] = React.useReducer(Reducer, {
    sortcolumn: null,
    data: foodRecommendationsData && foodRecommendationsData,
    direction: null,
  });
  // SUSPECTED DEFECT — REPORTED, NOT FIXED. Same as `../orders/index.tsx` and
  // `../draftOrders/index.tsx`: the reducer writes `sortcolumn`, nothing writes
  // `column`, so `column={column}` on the table render below is always
  // `undefined` and no header shows its sort caret. All three food tables carry
  // this. Left as-is because renaming it would turn the carets on.
  const { data, direction } = state;
  const column: string | undefined = undefined;
  const _selectedOrder =
    selectedOrdersLength === 1 &&
    foodRecommendationsData &&
    _.filter(foodRecommendationsData, { id: selectedOrderIds[0] })[0];

  return (
    <div style={{ minHeight: '65vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Form autoComplete="off">
          <Form.Group>
            <Form.Input
              placeholder="Search by Order Id"
              icon={<Icon name="search" onClick={handleSearchById} />}
              value={searchId}
              onChange={(e, searchProps) => setSearchId(searchProps.value)}
              name="searchId"
            />

            <DatesRangeInput
              name="datesRange"
              placeholder="Filter by Date Range"
              value={dateState}
              iconPosition="left"
              dateFormat="YYYY-MM-DD"
              onChange={handleChangeDate}
            />

            <Form.Button content="Clear" color="blue" onClick={resetFilters} />
          </Form.Group>
        </Form>
        <span style={{ float: 'right' }}>
          <span className="btn-group">
            {selectedOrdersLength > 0 && (
              <>
                <Confirm
                  open={cancelConfirm}
                  // onConfirm={cancelOrders}
                  confirmButton={<Button content="Yes, Cancel Order" negative />}
                  cancelButton={<Button content="Do not Cancel" positive />}
                  onCancel={() => setCancelConfirm(false)}
                />
              </>
            )}
          </span>
        </span>
      </div>
      <div>
        {((!foodRecommendationsData && !foodRecommendationsError) || loading || datasetLoading) && (
          <Loader active inline="centered" />
        )}
        {data && data.length === 0 && <Message warning header="Info" content="0 Items found!" />}
        {foodRecommendationsError && (
          <Message header="Oops!. Something went wrong.." content={foodRecommendationsError.message} negative />
        )}
        {ordersUpdateError && (
          <Message
            header="Oops!. Something went wrong.."
            content="Error updating draft order..Please check logs"
            negative
          />
        )}

        {data && data.length > 0 && (
          <>
            {ordersMessage.length > 0 && (
              <>
                <Message
                  icon
                  color="red"
                  negative={ordersMessage.toLowerCase().includes('error')}
                  positive={ordersMessage.toLowerCase().includes(' successful')}
                  hidden={hideMessage}
                >
                  {working && (
                    <>
                      <Icon name="circle notched" loading />
                      <Message.Content>
                        <Message.Header>Just one second</Message.Header>
                        {workingon}{' '}
                        {selectedOrderIds.length > 1 ? selectedOrderIds.length.toString() + ' orders' : 'the order'}
                        ...
                      </Message.Content>
                    </>
                  )}
                  {!working && ordersMessage.length > 0 && (
                    <>
                      <Message.Content>
                        <Message.Header>
                          {ordersMessage.toLowerCase().includes(' successful')
                            ? workingon + ' Success'
                            : workingon + ' Error'}
                        </Message.Header>
                        {ordersMessage}
                      </Message.Content>
                      <Icon name="close" onClick={() => setHideMessage(true)} />
                    </>
                  )}
                </Message>
              </>
            )}
            <FoodRecommendationsTable
              patientId={props.patientId}
              org={props.patient && props.patient.org}
              vendor={props.vendor}
              data={data}
              patients={patients && patients}
              openSelectedPatient={openSelectedPatient}
              selectedOrderIds={selectedOrderIds as string[]}
              column={column}
              direction={direction}
              addExportData={addExportData}
              selectAll={selectAll}
              dispatch={dispatch}
            />
          </>
        )}
      </div>
    </div>
  );
}
export default FoodRecommendations;
